/**
 * Root level application component
 *
 * @module components/App/AppComponent
 */

import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { IntlProvider } from 'react-intl';
import { StickyContainer } from 'react-sticky';

import AnonWelcome from '../../components/AnonWelcome/AnonWelcome';
import AppBg from '../../components/AppBg/AppBg';
import MetaTags from '../../components/MetaTags/MetaTags';
import Notifications from '../../components/Notifications/Notifications';
import { AriaRoles } from '../../constants/AccessibilityConstants';
import BlueBarContainer from '../../containers/BlueBar/BlueBar';
import Dropdown from '../../containers/Dropdown/Dropdown';
import NavContainer from '../../containers/Nav/Nav';
import OverlayContainer from '../../containers/OverlayContainer/OverlayContainer';
import { AsyncDomain } from '../../domains';

export default class extends React.Component {
  /**
   * @property {Node} children - the children nodes appended by React Router
   * @property {boolean} showDisplayAds - should display ads be shown
   * @property {boolean} showAnonWelcome - should display anonymous welcome component
   * @property {boolean} showSplashScreen - should splash be shown
   * @property {boolean} showTuner - should the mini tuner be shown
   * @property {boolean} showTopNav - should the top nav be shown
   * @property {boolean} showSearchInHeader - should the search modal be present in the header?
   * @property {string} locale - localization/internationalization prop
   * @property {object} i18nMessages - localization/internationalization prop
   * @property {object} params - url params
   * @property {boolean} isDebugMenuEnabled - should we display the debug menu?
   * @property {boolean} isRightRailActive - is a user ad-supported and have a short browser window?
   */
  static propTypes = {
    children: PropTypes.node,
    showDisplayAds: PropTypes.bool.isRequired,
    shouldLoadSimStream: PropTypes.bool.isRequired,
    shouldLoadRewards: PropTypes.bool.isRequired,
    showAnonWelcome: PropTypes.bool,
    showPremiumWelcome: PropTypes.bool,
    showSplashScreen: PropTypes.bool,
    showTuner: PropTypes.bool,
    showTopNav: PropTypes.bool,
    showSearchInHeader: PropTypes.bool,
    locale: PropTypes.string,
    i18nMessages: PropTypes.object,
    params: PropTypes.shape({
      stationId: PropTypes.string,
      trackToken: PropTypes.string,
      seoToken: PropTypes.string,
    }),
    isDebugMenuEnabled: PropTypes.bool,
    isListenerSupportMenuEnabled: PropTypes.bool,
    listenerTier: PropTypes.string,
    isRightRailActive: PropTypes.bool,
    shouldUseWakeLock: PropTypes.bool,
    defaultLocale: PropTypes.string,
    isStatsTestLoggerEnabled: PropTypes.bool.isRequired,
    shouldShowPremiumFeatures: PropTypes.bool,
    showColdStart: PropTypes.bool,
    shouldSkipStatsLogging: PropTypes.bool.isRequired,
    isAnonymousListener: PropTypes.bool,
    isSearchOpen: PropTypes.bool,
    shouldDropSearchToNextLine: PropTypes.bool,
  };

  static defaultProps = {
    showSplashScreen: true,
    showTuner: true,
    showTopNav: true,
    showOutdatedBrowserBar: false,
    isDebugMenuEnabled: false,
    isListenerSupportMenuEnabled: false,
    shouldShowPremiumFeatures: false,
  };

  /**
   * Retrieves the splash screen or the application depending on props
   *
   * @returns {XML} splash screen or app content
   */
  getContent = () => {
    const regionTopBar = classNames('region-topBar', {
      'region-topBar--rightRail': this.props.isRightRailActive,
      'region-topBar--shouldDropSearchToNextLine': this.props.shouldDropSearchToNextLine,
      'region-topBar--searchT3': true,
    });

    const regionMain = classNames('region-main', {
      'region-main--rightRail': this.props.isRightRailActive,
    });

    const regionBottomBar = classNames('region-bottomBar', {
      'region-bottomBar--rightRail': this.props.isRightRailActive,
    });

    return (
      <div className='App'>
        <MetaTags />
        {this.props.isStatsTestLoggerEnabled && !this.props.shouldSkipStatsLogging && (
          <AsyncDomain domain='stats' component='StatsTestLogger' />
        )}
        {this.props.shouldLoadRewards && <AsyncDomain domain='rewards' />}
        {/* developer debug menu enabled at buildtime by setting debugMenuEnabled: true*/}
        {this.props.isDebugMenuEnabled && (
          <AsyncDomain domain='debug' key='DebugMenu' component='DebugMenu' full={true} />
        )}
        {/* listener support menu enabled by appending ?support=true to the URL */}
        {this.props.isListenerSupportMenuEnabled && (
          <AsyncDomain domain='debug' key='DebugMenu' component='DebugMenu' />
        )}
        <div className='region-overlay'>
          <OverlayContainer />
        </div>
        <div className='region-dropdown'>
          <Dropdown />
        </div>
        <AppBg />
        {this.props.showTopNav && (
          <div className={regionTopBar} role={AriaRoles.BANNER}>
            <NavContainer
              showTopNav={this.props.showTopNav}
              showSearchInHeader={this.props.showSearchInHeader}
              showColdStart={this.props.showColdStart}
            />
            <BlueBarContainer />
          </div>
        )}
        <main className={regionMain} role={AriaRoles.MAIN}>
          {/* StickyContainer necessary for Sticky header in Collection Page */}
          <StickyContainer className='region-main__content'>{this.props.children}</StickyContainer>
        </main>

        {this.props.showDisplayAds && [
          <AsyncDomain
            domain='displayAds'
            key='displayAdController'
            component='DisplayAdController'
          />,
        ]}

        {!this.props.shouldShowPremiumFeatures && [
          <div className='region-coachmark' id='region-coachmark' key='region-coachmark' />,
        ]}

        {this.props.showAnonWelcome && [<AnonWelcome key='anonWelcome' />]}

        {this.props.shouldUseWakeLock && (
          <AsyncDomain domain='playback' key='WakeLock' component='WakeLock' />
        )}

        {<Notifications />}

        {this.props.showTuner && (
          <div key='region-bottom-bar' className={regionBottomBar}>
            <AsyncDomain domain='playback' key='tuner-controls' component='Tuner' />
          </div>
        )}
      </div>
    );
  };

  render() {
    return (
      <IntlProvider
        defaultLocale={this.props.defaultLocale}
        locale={this.props.locale}
        messages={this.props.i18nMessages}
      >
        {this.getContent()}
      </IntlProvider>
    );
  }
}
